import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiMapPin } from "react-icons/fi";
import siteConfig from "../../config/siteConfig";
import Accordion from "../common/Accordion";
import BrandMark from "../common/BrandMark";
import classNames from "../common/classNames";

const FooterLink = ({ href, children, ...props }) => {
  if (href?.startsWith("/")) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
};

const FooterLinks = ({ links = [] }) => (
  <ul className="site-footer__link-list">
    {links.map((link) => (
      <li key={`${link.label}-${link.href}`}>
        <FooterLink href={link.href}>{link.label}</FooterLink>
      </li>
    ))}
  </ul>
);

const useMobileFooter = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? false
      : window.matchMedia("(max-width: 767px)").matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const update = (event) => setIsMobile(event.matches);
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", update);
    return () => mediaQuery.removeEventListener?.("change", update);
  }, []);

  return isMobile;
};

const Footer = ({ className }) => {
  const isMobile = useMobileFooter();
  const footerGroups = siteConfig.footerNavigation || [];
  const address =
    siteConfig.contact?.address?.formatted || siteConfig.contact?.address;

  return (
    <footer className={classNames("site-footer", className)}>
      <div className="site-container site-footer__main">
        <section className="site-footer__brand">
          <BrandMark inverse linked={false} showTagline />
          <p>{siteConfig.description}</p>
          <address>
            {address && (
              <span>
                <FiMapPin aria-hidden="true" /> {address}
              </span>
            )}
            {siteConfig.contact?.email && (
              <a href={`mailto:${siteConfig.contact.email}`}>
                <FiMail aria-hidden="true" /> {siteConfig.contact.email}
              </a>
            )}
          </address>
        </section>

        <nav className="site-footer__navigation" aria-label="Footer navigation">
          {footerGroups.map((group) =>
            isMobile ? (
              <Accordion
                className="site-footer__accordion"
                title={group.title}
                headingLevel={2}
                key={group.title}
              >
                <FooterLinks links={group.links} />
              </Accordion>
            ) : (
              <section className="site-footer__group" key={group.title}>
                <h2>{group.title}</h2>
                <FooterLinks links={group.links} />
              </section>
            ),
          )}
        </nav>
      </div>

      <div className="site-footer__bottom">
        <div className="site-container site-footer__bottom-inner">
          <p>
            © {new Date().getFullYear()} {siteConfig.brandName}. All rights
            reserved.
          </p>
          <nav aria-label="Legal links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/cookies">Cookies</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/accessibility">Accessibility</Link>
          </nav>
          <p className="site-footer__payment-note">
            Secure checkout · Visa · Mastercard · American Express · UnionPay
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
